import React from "react";
import {Col, Row} from "react-bootstrap";
import {RBSModal} from "../../base/rbs_components/RBSModal.tsx";
import {RBSTextField} from "../../base/rbs_components/RBSTextField.tsx";
import {RBSDropdown} from "../../base/rbs_components/RBSDropdown.tsx";
import type {Transaction, TransactionInput} from "../../base/ui_general/GeneralInterfaces.tsx";
import {
    LabelTextMessageTypes,
    PaymentFrequencies,
    RBSVariantEnum,
    TextfieldInputTypes,
    TransactionTypes,
    UserLanguages,
    ValidationSchemes
} from "../../base/Settings.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import {getLabelTextMessage, getNextQuarterEnd, getNextQuarterStart, formatDateToStringInLocalTZ} from '../../base/utils/GeneralUtilities.tsx';


interface TransactionDetailModalProps {
    /** Indicates whether the modal is visible. */
    show: boolean;
    /** The transaction to display or edit. */
    transaction?: Transaction;
    /** Callback to close the modal. */
    onClose: () => void;
    /** Callback to submit the transaction input. */
    onSubmit: (transactionInput: TransactionInput) => void;
    /** Indicates if the form input is valid. */
    isValid: boolean;
    /** InputStateControl instance for managing input state and validation. */
    inputStateControl: InputStateControl;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
}

/**
 * TransactionDetailModal displays and allows editing of a Transaction's fields in a modal dialog.
 */
export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = (props: TransactionDetailModalProps) => {
    // Calculate default dates for creation mode
    const today = new Date();
    const default_effective_date = formatDateToStringInLocalTZ(getNextQuarterStart(today));
    const default_first_payment_date = formatDateToStringInLocalTZ(getNextQuarterEnd(today));
    const maturityDate = new Date(default_first_payment_date);
    maturityDate.setFullYear(maturityDate.getFullYear() + 10);
    const default_maturity_date = formatDateToStringInLocalTZ(maturityDate);

    // Prefill values from transaction if available, otherwise use defaults for dates, type, and frequency
    const prefill = (key: keyof Transaction): string => {
        if (props.transaction) {
            return String(props.transaction[key] ?? "");
        }
        // Only for creation mode, use calculated defaults for date fields, type, and frequency
        switch (key) {
            case "effective_date":
                return default_effective_date;
            case "first_payment_date":
                return default_first_payment_date;
            case "maturity_date":
                return default_maturity_date;
            case "transaction_type":
                return TransactionTypes.CASH;
            case "payment_frequency":
                return PaymentFrequencies.QUARTERLY;
            default:
                return "";
        }
    };

    // Choose header title based on whether transaction is provided
    const headerTitle = props.transaction
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_TRANSACTION_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_TITLE, props.user_language);

    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                // Compose TransactionInput from inputStateControl
                const transactionInput: TransactionInput = {
                    name: props.inputStateControl.getStringValueForKey("transaction_name") as string,
                    description: props.inputStateControl.getStringValueForKey("transaction_description") as string,
                    effective_date: props.inputStateControl.getStringValueForKey("transaction_effective_date") as string,
                    first_payment_date: props.inputStateControl.getStringValueForKey("transaction_first_payment_date") as string,
                    maturity_date: props.inputStateControl.getStringValueForKey("transaction_maturity_date") as string,
                    payment_frequency: props.inputStateControl.getStringValueForKey("payment_frequency") as PaymentFrequencies,
                    transaction_type: props.inputStateControl.getStringValueForKey("transaction_type") as TransactionTypes,
                };
                props.onSubmit(transactionInput);
            }}
        >
            {/* Row 1: Name, Type, Frequency */}
            <Row>
                <Col md={4} xs={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_NAME, props.user_language)}
                        input_key="transaction_name"
                        prefill={prefill("name") || "New Transaction"}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                    />
                </Col>
                <Col md={4} xs={12}>
                    {/* Transaction Type Dropdown */}
                    <RBSDropdown
                        input_key="transaction_type"
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_TYPE, props.user_language)}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        prefill={props.inputStateControl.getStringValueForKey("transaction_type") || prefill("transaction_type")}
                        disabled={!!props.transaction}
                        items={Object.values(TransactionTypes).map(tt => ({ label: tt, value: tt }))}
                    />
                </Col>
                <Col md={4} xs={12}>
                    {/* Payment Frequency Dropdown */}
                    <RBSDropdown
                        input_key="payment_frequency"
                        variant={RBSVariantEnum.DANGER}
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_FREQUENCY, props.user_language)}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        prefill={props.inputStateControl.getStringValueForKey("payment_frequency") || prefill("payment_frequency")}
                        items={Object.values(PaymentFrequencies).map(freq => ({ label: freq, value: freq }))}
                    />
                </Col>
            </Row>
            {/* Row 2: Effective Date, First Payment Date, Maturity Date */}
            <Row>
                <Col md={4} xs={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_EFFECTIVE_DATE, props.user_language)}
                        input_key="transaction_effective_date"
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.DATE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.DATE}
                        prefill={prefill("effective_date")}
                    />
                </Col>
                <Col md={4} xs={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_FIRST_PAYMENT, props.user_language)}
                        input_key="transaction_first_payment_date"
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.DATE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.DATE}
                        prefill={prefill("first_payment_date")}
                    />
                </Col>
                <Col md={4} xs={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_MATURITY_DATE, props.user_language)}
                        input_key="transaction_maturity_date"
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.DATE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.DATE}
                        prefill={prefill("maturity_date")}
                    />
                </Col>
            </Row>
            {/* Row 3: Description (full width) */}
            <Row>
                <Col xs={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NEW_TRANSACTION_CREATION_DESCRIPTION, props.user_language)}
                        input_key="transaction_description"
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        prefill={prefill("description") || "This is a demo transaction."}
                        multiline={true}
                        style={{minHeight: 300}}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
